import { ISelectItem, Pill, Tile, useSettings } from "@react-gcc-eds/core";
import { JSX, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { IInternalItemsRedirectionFilters } from "../../../../actions/internal-items-filters-actions";
import { IInternalOrdersRedirectionFilters } from "../../../../actions/internal-orders-filters-actions";
import { useMetricsActions } from "../../../../actions/metrics-actions";
import { useMetricsFiltersActions } from "../../../../actions/metrics-filters-actions";
import { useStore } from "../../../../store";
import DatePicker from "../../../filters/date-picker";

import cx from "classnames";
import { DashboardGraph, IDashboardItemSelected } from "..";
import { useSettingsActions } from "../../../../actions/settings-actions";
import { dateAsYMDString } from "../../../../utils";
import { useLocalization } from "../../../../utils/localization";
import { DashboardTooltipHeader } from "../../../shared/tooltip-header";
import { DashboardTileAlignment as TileAlignment } from "../index";

import { usePredefinedRanges } from "../../../filters/date-picker/predefined-ranges";
import CustomTabs, { ITab } from "../../../shared/custom-tabs";
import { StatusBarChart } from "../bars-chart";
import { InternalPurchaseOrdersFilterDropDown } from "./filters";

/**
 * The Dashboard "Internal Overall order status" barchart tile.
 */
const OverallInternalOrderStatusBars = ({
  labels,
  className,
  customerId,
  alignment,
  onItemSelected
}: {
  labels: { [key: string]: string | undefined };
  className: string;
  customerId: string;
  alignment: TileAlignment;
  onItemSelected?(data: IDashboardItemSelected): void;
}) => {
  const [state] = useStore();
  const [selectedTab, setSelectedTab] = useState<ITab>();
  const [tabs, setTabs] = useState<ITab[]>([]);
  const [actions, setActions] = useState<JSX.Element[]>([]);
  const tileRef = useRef<HTMLDivElement>(null);
  const { currentCustomer } = useSettingsActions();
  const { rangeAsString, largestRange, isLargestRange } = usePredefinedRanges(
    new Date(currentCustomer().dataAvailableFromDate)
  );
  const {
    getInternalOrderCreationDateBarsMetric,
    getInternalItemCreationDateBarsMetric,
    getInternalItemCreationPurchaseOrderNumbers
  } = useMetricsActions();
  const {
    setMetricsOverallInternalOrderStatusBarsCreationRange,
    setMetricsOverallInternalOrderStatusBarsOrderNumbers
  } = useMetricsFiltersActions();
  const { theme } = useSettings();
  const { localeString } = useLocalization();
  const {
    internalOrderCreationDateBarsMetric: orderCreationDateBarsMetricLoading,
    internalItemCreationDateBarsMetric: itemCreationDateBarsMetricLoading
  } = state.fetchingData.metrics;

  const maxWidth = useMemo(() => {
    if (tileRef.current) {
      return tileRef.current.clientWidth;
    }
  }, [tileRef.current, window.innerWidth]);

  useEffect(() => {
    if (state.metricsFilters.overallInternalOrderStatusBarsCreationRange) {
      const { from, to } = state.metricsFilters.overallInternalOrderStatusBarsCreationRange;

      getInternalOrderCreationDateBarsMetric(dateAsYMDString(from), dateAsYMDString(to));
    }
  }, [state.metricsFilters.overallInternalOrderStatusBarsCreationRange, customerId]);

  useEffect(() => {
    if (state.metricsFilters.overallInternalOrderStatusBarsCreationRange) {
      const {
        overallInternalOrderStatusBarsCreationRange: { from, to },
        overallInternalOrderStatusBarsItemsOrderNumbers
      } = state.metricsFilters;

      getInternalItemCreationDateBarsMetric(
        dateAsYMDString(from),
        dateAsYMDString(to),
        overallInternalOrderStatusBarsItemsOrderNumbers
          ? overallInternalOrderStatusBarsItemsOrderNumbers
          : []
      );
    }
  }, [
    state.metricsFilters.overallInternalOrderStatusBarsCreationRange,
    state.metricsFilters.overallInternalOrderStatusBarsItemsOrderNumbers,
    customerId
  ]);

  useEffect(() => {
    if (state.metricsFilters.overallInternalOrderStatusBarsCreationRange) {
      const {
        overallInternalOrderStatusBarsCreationRange: { from, to }
      } = state.metricsFilters;

      getInternalItemCreationPurchaseOrderNumbers(dateAsYMDString(from), dateAsYMDString(to));
    }
  }, [state.metricsFilters.overallInternalOrderStatusBarsCreationRange]);

  useEffect(() => {
    const tabs = [];

    if (labels["internalPurchaseOrders"] !== undefined) {
      tabs.push(purchaseOrderTab);
    }
    if (labels["internalPurchaseOrderItems"] !== undefined) {
      tabs.push(purchaseOrderItemsTab);
    }
    if (tabs.length) {
      setTabs(tabs);
      if (!selectedTab) {
        setSelectedTab(tabs[0]);
      }
    }
  }, [orderCreationDateBarsMetricLoading, itemCreationDateBarsMetricLoading]);

  useEffect(() => {
    const myActions = [];

    switch (selectedTab?.title) {
      case labels["internalPurchaseOrders"]:
        myActions.push(dateFilter);
        break;
      case labels["internalPurchaseOrderItems"]:
        myActions.push(dateFilter);
        myActions.push(
          <InternalPurchaseOrdersFilterDropDown
            onChange={setMetricsOverallInternalOrderStatusBarsOrderNumbers}
            selectedItems={getSelectedOrders()}
            key={"orders-filter"}
          />
        );
        break;
      default:
        return;
    }

    setActions(myActions);
  }, [selectedTab]);

  const PurchaseOrderBarChart = useCallback(() => {
    const { internalOrderCreationDateBarsMetric } = state.metrics;

    return (
      <StatusBarChart
        metrics={internalOrderCreationDateBarsMetric}
        loading={orderCreationDateBarsMetricLoading === true}
        unit={""}
        datesRange={state.metricsFilters.overallInternalOrderStatusBarsCreationRange}
        internal
        onSelection={(filters: IInternalOrdersRedirectionFilters) =>
          onItemSelected && onItemSelected({ graph: DashboardGraph.InternalOrders, filters })
        }
      />
    );
  }, [
    orderCreationDateBarsMetricLoading,
    state.metrics.internalOrderCreationDateBarsMetric,
    state.metricsFilters.overallInternalOrderStatusBarsCreationRange
  ]);

  const PurchaseOrdersItemsBarChart = useCallback(() => {
    const { internalItemCreationDateBarsMetric } = state.metrics;

    return (
      <StatusBarChart
        metrics={internalItemCreationDateBarsMetric}
        loading={itemCreationDateBarsMetricLoading === true}
        unit={""}
        datesRange={state.metricsFilters.overallInternalOrderStatusBarsCreationRange}
        internal
        onSelection={(barFilters: IInternalItemsRedirectionFilters) => {
          if (onItemSelected) {
            onItemSelected({
              graph: DashboardGraph.InternalItems,
              filters: {
                ...barFilters,
                internalPurchaseOrderNumber:
                  state.metricsFilters.overallInternalOrderStatusBarsItemsOrderNumbers
              }
            });
          }
        }}
      />
    );
  }, [
    itemCreationDateBarsMetricLoading,
    state.metrics.internalItemCreationDateBarsMetric,
    state.metricsFilters.overallInternalOrderStatusBarsCreationRange
  ]);

  const tileHeader = (
    <div className="dashboard-tile">
      <DashboardTooltipHeader
        header={
          localeString("Dashboard_InternalOverallOrderStatusBarChart") ||
          "Internal Overall order status"
        }
        tooltipInfo="Graph is showing the status for the selected time period"
        alignment={alignment}
      />
    </div>
  );

  const resetFilter = () => {
    setMetricsOverallInternalOrderStatusBarsCreationRange(largestRange.selector());
  };

  const resetFilterPill = () => {
    return (
      <Pill className={cx("filter-pill", theme)} onToggle={resetFilter}>
        Reset filters
      </Pill>
    );
  };

  const filtersSummary = (
    <div className="dashboard-active-filter">
      <Pill>{rangeAsString(state.metricsFilters.overallInternalOrderStatusBarsCreationRange)}</Pill>
      {!isLargestRange(state.metricsFilters.overallInternalOrderStatusBarsCreationRange)
        ? resetFilterPill()
        : null}
    </div>
  );

  const purchaseOrderTab = {
    view: PurchaseOrderBarChart(),
    title: labels["internalPurchaseOrders"],
    index: 0
  } as ITab;

  const purchaseOrderItemsTab = {
    view: PurchaseOrdersItemsBarChart(),
    title: labels["internalPurchaseOrderItems"],
    index: 1
  } as ITab;

  const dateFilter = (
    <DatePicker
      dual={state.userPreferences.useDualCalendar}
      rolling
      selectedRange={state.metricsFilters.overallInternalOrderStatusBarsCreationRange}
      oldestDate={new Date(currentCustomer().dataAvailableFromDate)}
      onRangeSelected={setMetricsOverallInternalOrderStatusBarsCreationRange}
      right={alignment !== TileAlignment.LEFT}
      key="date-picker"
    />
  );

  const getSelectedOrders = () => {
    const items = state.metricsFilters.overallInternalOrderStatusBarsItemsOrderNumbers
      ? state.metricsFilters.overallInternalOrderStatusBarsItemsOrderNumbers.map(
          po => ({ value: po, title: po }) as ISelectItem
        )
      : [];
    return items;
  };
  return (
    <>
      <Tile
        className={`${className}`}
        lg={alignment === TileAlignment.FULL ? 12 : 6}
        sm={12}
        title={tileHeader}
        subtitle={filtersSummary}
        actions={actions}
      >
        <CustomTabs onTabChange={(tab: ITab) => setSelectedTab(tab)} tabs={tabs} />
      </Tile>
    </>
  );
};

export default OverallInternalOrderStatusBars;
